import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  createOrganizationRole,
  deleteOrganizationRole,
  listOrganizationRoles,
  updateOrganizationRole,
} from '../../services/organizationUserService';

const OrganizationRolesPage = () => {
  const authUser = useSelector((state) => state.auth?.user);
  const isOrgAdmin = Boolean(
    authUser?.is_organization_admin ?? authUser?.is_organization_owner
  );
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyRoleId, setBusyRoleId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', description: '' });
  const [editForm, setEditForm] = useState({ name: '', slug: '', description: '' });

  const defaultRole = useMemo(
    () => roles.find((r) => r.is_default) ?? null,
    [roles]
  );

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listOrganizationRoles();
      const list = Array.isArray(data?.roles)
        ? data.roles
        : Array.isArray(data)
          ? data
          : [];
      setRoles(list);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not load organization roles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusyRoleId('create');
    try {
      const result = await createOrganizationRole({
        name: createForm.name.trim(),
        slug: createForm.slug.trim() || undefined,
        description: createForm.description.trim() || undefined,
      });
      setSuccess(result?.message || 'Role created successfully.');
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', description: '' });
      await loadRoles();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not create role.');
    } finally {
      setBusyRoleId(null);
    }
  };

  const startEdit = (role) => {
    setEditingRole(role);
    setEditForm({
      name: role.name || '',
      slug: role.slug || '',
      description: role.description || '',
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingRole) return;
    setError('');
    setSuccess('');
    setBusyRoleId(editingRole.id);
    try {
      const payload = {};
      if (editForm.name.trim() !== '') payload.name = editForm.name.trim();
      payload.slug = editForm.slug.trim() || null;
      payload.description = editForm.description.trim() || null;
      const result = await updateOrganizationRole(editingRole.id, payload);
      setSuccess(result?.message || 'Role updated successfully.');
      setShowEdit(false);
      setEditingRole(null);
      await loadRoles();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not update role.');
    } finally {
      setBusyRoleId(null);
    }
  };

  const handleDelete = async (role) => {
    if (role.is_default) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    setError('');
    setSuccess('');
    setBusyRoleId(role.id);
    try {
      const result = await deleteOrganizationRole(role.id);
      setSuccess(result?.message || 'Role deleted successfully.');
      await loadRoles();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not delete role.');
    } finally {
      setBusyRoleId(null);
    }
  };

  return (
    <div className="organization-roles-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-semibold mb-1">Organization roles</h4>
          <p className="text-muted mb-0">Create, edit, and manage organization roles.</p>
        </div>
        {isOrgAdmin && (
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <i className="ri-add-line me-2" />
            Create role
          </Button>
        )}
      </div>

      {!isOrgAdmin && (
        <Alert variant="warning">Only organization admins can manage roles.</Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th className="ps-3">Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Default</th>
                  <th className="text-end pe-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      No roles found for this organization.
                    </td>
                  </tr>
                )}
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td className="ps-3 fw-medium">{role.name}</td>
                    <td className="text-muted">{role.slug || '—'}</td>
                    <td>
                      <Badge bg={role.status === 'active' ? 'success' : 'secondary'}>
                        {role.status || 'unknown'}
                      </Badge>
                    </td>
                    <td>
                      {role.is_default ? (
                        <Badge bg="primary">Default</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end pe-3">
                      {isOrgAdmin ? (
                        <div className="d-inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={busyRoleId === role.id}
                            onClick={() => startEdit(role)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={busyRoleId === role.id || role.is_default}
                            onClick={() => handleDelete(role)}
                            title={role.is_default ? 'Default role cannot be deleted' : ''}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted small">No permission</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={createForm.name}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Slug (optional)</Form.Label>
              <Form.Control
                value={createForm.slug}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="Auto-generated if empty"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={createForm.description}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busyRoleId === 'create'}>
              {busyRoleId === 'create' ? 'Creating...' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                value={editForm.slug}
                onChange={(e) => setEditForm((prev) => ({ ...prev, slug: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>
            {defaultRole?.id === editingRole?.id && (
              <Alert variant="info" className="mt-3 mb-0 small">
                This is the default role.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busyRoleId === editingRole?.id}>
              {busyRoleId === editingRole?.id ? 'Saving...' : 'Save changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationRolesPage;
